/**
 * FigmaSportsShell — desktop 3-column shell + mobile sportsbook chrome
 */
import React, { useState, useCallback, useEffect, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReceiptText, Search, Star } from 'lucide-react';
import FigmaLeftRail from './FigmaLeftRail';
import FigmaDomesticBoard from './FigmaDomesticBoard';
import FigmaInternationalBoard from './FigmaInternationalBoard';
import FigmaBetCart from './FigmaBetCart';
import getSportIconUrl from './sportIconResolver';
import sportsAllIcon from './assets/sportsall.png';
import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsnewContext } from '../../../_context/SportsnewContext';
import InternationalPage from './InternationalPage';
import InplayPage from './InplayPage';
import DomesticPage from './DomesticPage';
import EsportsPage from './EsportsPage';

const BETTING_TYPES = [
  { key: 'DOMESTIC', label: '국내형' },
  { key: 'SPECIAL', label: 'E-스포츠' },
  { key: 'INTERNATIONAL', label: '해외형' },
  { key: 'LIVE', label: '인플레이' },
];

const modeForBettingType = (type) => (type === 'LIVE' ? 'live' : 'prematch');
const scopeForBettingType = (type) => {
  if (type === 'LIVE') return 'live';
  if (type === 'DOMESTIC') return 'domestic';
  if (type === 'SPECIAL') return 'esports';
  return 'overseas';
};

const useMobileSportsShell = (breakpoint = 575) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const sync = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [breakpoint]);

  return isMobile;
};

const FigmaSportsShell = (props) => {
  const {
    bettingType,
    setBettingType,
    liveCount,
    matchCounts,
    matchTotal,
    searchQuery,
    setSearchQuery,
    sortOrder,
    setSortOrder,
    SportsLiveComponent,
    ...boardProps
  } = props;

  const isMobile = useMobileSportsShell();
  const isMobileCart = useMobileSportsShell(960);
  const navigate = useNavigate();
  const { selections } = useContext(SportsBetCartContext);
  const {
    setMode: setSportsnewMode,
    setDisplayScope: setSportsnewDisplayScope,
    setSelectedSport: setSportsnewSport,
    setSelectedLeagueId: setSportsnewLeagueId,
    setSelectedGameTitleId: setSportsnewGameTitleId,
    setSelectedTournamentId: setSportsnewTournamentId,
  } = useContext(SportsnewContext) || {};
  const activeMatchCountsTotal = (
    matchCounts?._betting_category && matchCounts._betting_category !== bettingType
      ? 0
      : Number(matchCounts?.total || 0)
  );
  const liveMatchCountsTotal = (
    matchCounts?._betting_category === 'LIVE'
      ? Number(matchCounts?.total || 0)
      : 0
  );
  const activeLiveTotal = bettingType === 'LIVE'
    ? (Number(props.totalCount || 0) || activeMatchCountsTotal || Number(matchTotal || 0))
    : 0;
  const liveTabCount = useMemo(() => (
    Math.max(
      0,
      Number(liveCount || 0),
      liveMatchCountsTotal,
      activeLiveTotal,
    )
  ), [activeLiveTotal, liveCount, liveMatchCountsTotal]);
  const showLiveTab = liveTabCount > 0 || bettingType === 'LIVE';

  const [localSearch, setLocalSearch] = useState(searchQuery || '');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCartBusy, setMobileCartBusy] = useState(false);
  const [previousMobileCart, setPreviousMobileCart] = useState(isMobileCart);
  const cartOpenerRef = useRef(null);
  const openMobileCart = useCallback((event) => {
    cartOpenerRef.current = event?.currentTarget ?? null;
    cartOpenerRef.current?.blur();
    setMobileMenuOpen(false);
    setMobileCartOpen(true);
  }, []);
  const closeMobileCart = useCallback(() => setMobileCartOpen(false), []);
  useEffect(() => {
    if (!mobileCartOpen && cartOpenerRef.current?.isConnected
      && cartOpenerRef.current.getClientRects().length) {
      cartOpenerRef.current.focus({ preventScroll: true });
      cartOpenerRef.current = null;
    }
  }, [mobileCartOpen]);
  if (previousMobileCart !== isMobileCart) {
    setPreviousMobileCart(isMobileCart);
    if (!isMobileCart && !mobileCartBusy && mobileCartOpen) setMobileCartOpen(false);
  }
  if (isMobileCart && mobileCartBusy && !mobileCartOpen) setMobileCartOpen(true);
  // Busy state never closes a manually opened cart when it finishes.

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [bettingType, props.selectedSport]);

  const latestBettingTypeRef = useRef(bettingType);
  const latestLiveCountRef = useRef(liveTabCount);
  const [mobileCounts, setMobileCounts] = useState({
    DOMESTIC: 0,
    SPECIAL: 0,
    INTERNATIONAL: 0,
    LIVE: liveTabCount,
  });

  useEffect(() => {
    latestBettingTypeRef.current = bettingType;
  }, [bettingType]);

  useEffect(() => {
    latestLiveCountRef.current = liveTabCount;
  }, [liveTabCount]);

  useEffect(() => {
    setLocalSearch(searchQuery || '');
  }, [searchQuery]);

  useEffect(() => {
    setMobileCounts((prev) => (
      prev.LIVE === liveTabCount ? prev : { ...prev, LIVE: liveTabCount }
    ));
  }, [liveTabCount]);

  // Mobile tab counts are no longer fetched from the old /sport API.
  // Tab counts come from SportsnewContext (liveCount + matchCounts).
  useEffect(() => {
    if (!isMobile) return undefined;
    setMobileCounts((prev) => ({ ...prev, LIVE: latestLiveCountRef.current }));
    return undefined;
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const activeType = latestBettingTypeRef.current;
    if (activeType === 'LIVE') {
      setMobileCounts((prev) => (
        prev.LIVE === liveTabCount ? prev : { ...prev, LIVE: liveTabCount }
      ));
      return;
    }
    if (matchCounts?._betting_category && matchCounts._betting_category !== activeType) return;
    const nextTotal = Number(matchCounts?.total || 0);
    setMobileCounts((prev) => (
      prev[activeType] === nextTotal
        ? prev
        : { ...prev, [activeType]: nextTotal }
    ));
  }, [isMobile, liveTabCount, matchCounts]);

  useEffect(() => {
    if (!isMobile) {
      setSearchOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const html = document.documentElement;
    const { body } = document;

    if (isMobile) {
      html.classList.add('zxsb_mobile_scroll_mode');
      body.classList.add('zxsb_mobile_scroll_mode');
    } else {
      html.classList.remove('zxsb_mobile_scroll_mode');
      body.classList.remove('zxsb_mobile_scroll_mode');
    }

    return () => {
      html.classList.remove('zxsb_mobile_scroll_mode');
      body.classList.remove('zxsb_mobile_scroll_mode');
    };
  }, [isMobile]);

  const handleSearchSubmit = useCallback(() => {
    setSearchQuery(localSearch);
  }, [localSearch, setSearchQuery]);

  const handleSearchKeyDown = useCallback((event) => {
    if (event.key === 'Enter') handleSearchSubmit();
  }, [handleSearchSubmit]);

  const resetSportsnewDrilldown = useCallback(() => {
    setSportsnewLeagueId?.(null);
    setSportsnewGameTitleId?.(null);
    setSportsnewTournamentId?.(null);
  }, [setSportsnewLeagueId, setSportsnewGameTitleId, setSportsnewTournamentId]);

  const syncSportsnewTopFilter = useCallback((nextType, sport = null) => {
    setSportsnewMode?.(modeForBettingType(nextType));
    setSportsnewDisplayScope?.(scopeForBettingType(nextType));
    resetSportsnewDrilldown();
    if (nextType === 'SPECIAL') {
      setSportsnewSport?.('esports');
      return;
    }
    const slug = sport?.slug || null;
    setSportsnewSport?.(slug ? { slug } : null);
  }, [resetSportsnewDrilldown, setSportsnewMode, setSportsnewDisplayScope, setSportsnewSport]);

  const handleModeChange = useCallback((nextType) => {
    if (nextType === 'LIVE') {
      props.setPopularMode?.(false);
      props.setSelectedSport?.(null);
    } else {
      props.setPopularMode?.(false);
      props.setSelectedSport?.(null);
    }
    syncSportsnewTopFilter(nextType);
    setBettingType(nextType);
    if (nextType === 'LIVE') {
      navigate('/sports?type=LIVE&page=1&live_page=1', { replace: true });
    } else {
      navigate(`/sports?type=${nextType}`, { replace: true });
    }
    setSearchOpen(false);
  }, [navigate, props.setPopularMode, props.setSelectedSport, setBettingType, syncSportsnewTopFilter]);

  const mobileModeTabs = useMemo(() => (
    BETTING_TYPES
      .filter((item) => item.key !== 'LIVE' || showLiveTab)
      .map((item) => ({
        key: item.key,
        label: item.label,
        count: mobileCounts[item.key] || 0,
      }))
  ), [mobileCounts, showLiveTab]);

  const mobileCategories = props.categories || [];
  const mobileSportChips = useMemo(() => {
    const base = mobileCategories.map((category) => ({
      key: category.id,
      label: category.name_ko || category.name,
      count: props.getCountForSport(category.id),
      active: !props.popularMode && Number(props.selectedSport?.id) === Number(category.id),
      onClick: () => {
        props.setPopularMode?.(false);
        props.setSelectedSport(category);
        syncSportsnewTopFilter(bettingType, category);
      },
      renderIcon: () => (
        getSportIconUrl(category)
          ? <img src={getSportIconUrl(category)} alt="" className="zxsb_m_sport_icon_img" />
          : props.getSportIcon(category)
      ),
      featured: false,
    }));

    return [
      {
        key: 'all',
        label: '전체',
        count: props.totalCount || 0,
        active: !props.popularMode && !props.selectedSport,
        onClick: () => {
          props.setPopularMode?.(false);
          props.setSelectedSport(null);
          syncSportsnewTopFilter(bettingType, null);
        },
        renderIcon: () => (
          <img src={sportsAllIcon} alt="" className="zxsb_m_sport_icon_img" />
        ),
        featured: false,
      },
      ...(bettingType === 'LIVE' ? [] : [
      {
        key: 'popular',
        label: '주요경기',
        count: props.totalCount || 0,
        active: Boolean(props.popularMode),
        onClick: () => {
          props.setPopularMode?.(true);
          props.setSelectedSport(null);
          syncSportsnewTopFilter(bettingType, null);
        },
        renderIcon: () => (
          <span className="zxsb_m_sport_featured_star"><Star/></span>
        ),
        featured: true,
      },
      ]),
      ...base,
    ];
  }, [
    bettingType,
    mobileCategories,
    props.getCountForSport,
    props.getSportIcon,
    props.popularMode,
    props.selectedSport,
    props.setPopularMode,
    props.setSelectedSport,
    props.totalCount,
    syncSportsnewTopFilter,
  ]);

  const mobileFolderTiers = useMemo(() => {
    if (props.effectiveBonusTiers?.length > 0) {
      return props.effectiveBonusTiers;
    }
    return [{ fold: 3 }, { fold: 4 }, { fold: 5 }, { fold: 6 }, { fold: 10 }];
  }, [props.effectiveBonusTiers]);

  const contentBoard = () => {
    if (bettingType === 'LIVE' && SportsLiveComponent) {
      return (
        <SportsLiveComponent
          mobileCartOpen={mobileCartOpen}
          onToggleMobileCart={() => setMobileCartOpen((prev) => !prev)}
          cartCount={selections.length}
        />
      );
    }

    if (bettingType === 'INTERNATIONAL') {
      return (
        <FigmaInternationalBoard
          bettingType={bettingType}
          liveCount={liveCount}
          matchCounts={matchCounts}
          matchTotal={matchTotal}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          mobileCartOpen={mobileCartOpen}
          onToggleMobileCart={() => setMobileCartOpen((prev) => !prev)}
          cartCount={selections.length}
          isMobile={isMobile}
          {...boardProps}
        />
      );
    }

    return (
      <FigmaDomesticBoard
        bettingType={bettingType}
        liveCount={liveCount}
        matchCounts={matchCounts}
        matchTotal={matchTotal}
        isMobile={isMobile}
        {...boardProps}
      />
    );
  };

  // if (!isMobile) {
  //   return (
  //     <div className="zxsb_page zxsb_page_mobile">
  //       <div className="zxsb_m_sports_row">
  //         {mobileSportChips.map((chip) => (
  //           <button
  //             key={chip.key}
  //             type="button"
  //             className={`zxsb_m_sport_chip ${chip.active ? 'zxsb_m_sport_chip_active' : ''} ${chip.featured ? 'zxsb_m_sport_chip_featured' : ''}`}
  //             onClick={chip.onClick}
  //           >
  //             <span className="zxsb_m_sport_icon">
  //               {chip.renderIcon()}
  //             </span>
  //             <span className="zxsb_m_sport_name">{chip.label}</span>
  //             <span className="zxsb_m_sport_count">{chip.count}</span>
  //           </button>
  //         ))}
  //       </div>

  //       <div className="zxsb_m_toolbar">
  //         <div className="zxsb_m_mode_tabs">
  //           {mobileModeTabs.map((tab) => (
  //             <button
  //               key={tab.key}
  //               type="button"
  //               className={`zxsb_m_mode_tab ${bettingType === tab.key ? 'zxsb_m_mode_tab_active' : ''}`}
  //               onClick={() => handleModeChange(tab.key)}
  //             >
  //               {tab.label} {tab.count}
  //             </button>
  //           ))}
  //         </div>

  //         <button
  //           type="button"
  //           className={`zxsb_m_search_toggle ${searchOpen ? 'zxsb_m_search_toggle_active' : ''}`}
  //           onClick={() => setSearchOpen((prev) => !prev)}
  //           aria-label="검색"
  //         >
  //           <Search size={22} strokeWidth={2} />
  //         </button>
  //       </div>

  //       {searchOpen && (
  //         <div className="zxsb_m_search_row">
  //           <input
  //             type="text"
  //             className="zxsb_m_search_input"
  //             placeholder="팀명 검색"
  //             value={localSearch}
  //             onChange={(event) => setLocalSearch(event.target.value)}
  //             onKeyDown={handleSearchKeyDown}
  //           />
  //           <button type="button" className="zxsb_m_search_btn" onClick={handleSearchSubmit}>
  //             검색
  //           </button>
  //         </div>
  //       )}

  //       {(bettingType === 'DOMESTIC' || bettingType === 'SPECIAL') && (
  //         <div className="zxsb_m_folders">
  //           {mobileFolderTiers.map((tier, index) => (
  //             <div
  //               key={`${tier.fold}-${index}`}
  //               className={`zxsb_m_folder ${props.activeBonusFold === tier.fold ? 'zxsb_m_folder_active' : ''}`}
  //             >
  //               <span>{tier.fold}폴더</span>
  //               <span className="zxsb_m_folder_dot" />
  //               <span className="zxsb_m_folder_val">
  //                 {tier.value != null ? `x${Number(tier.value).toFixed(2)}` : '보너스'}
  //               </span>
  //             </div>
  //           ))}
  //         </div>
  //       )}

  //       <div className="zxsb_m_content">
  //         {contentBoard()}
  //       </div>

  //       <FigmaBetCart
  //         isMobile
  //         mobileOpen={mobileCartOpen}
  //         onMobileClose={() => setMobileCartOpen(false)}
  //       />

  //       <button
  //         type="button"
  //         className={`zxsb_m_cart_fab ${mobileCartOpen ? 'zxsb_m_cart_fab_active' : ''}`}
  //         onClick={() => setMobileCartOpen((prev) => !prev)}
  //       >
  //         <span className="zxsb_m_bottom_icon_wrap">
  //           <ReceiptText size={16} strokeWidth={2.1} />
  //           {selections.length > 0 && <span className="zxsb_m_bottom_badge">{selections.length}</span>}
  //         </span>
  //         <span>베팅카트</span>
  //       </button>
  //     </div>
  //   );
  // }

  return (
    <div className="zxsb_page" inert={mobileCartOpen ? '' : undefined} aria-hidden={mobileCartOpen || undefined}>
      {/* Floating sports menu button for mobile view */}
      {isMobile && (
        <button
          type="button"
          className="zxsb_mobile_sidebar_toggle"
          onClick={() => setMobileMenuOpen(prev => !prev)}
        >
          {mobileMenuOpen ? '✕ 닫기' : '☰ 스포츠 메뉴'}
        </button>
      )}

      {/* Scrim overlay for mobile sidebar */}
      {isMobile && mobileMenuOpen && (
        <div
          className="zxsb_mobile_sidebar_scrim"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="zxsb_shell">
        <FigmaLeftRail
          categories={props.categories}
          selectedSport={props.selectedSport}
          setSelectedSport={props.setSelectedSport}
          totalCount={props.totalCount}
          getCountForSport={props.getCountForSport}
          getSportIcon={props.getSportIcon}
          bettingType={bettingType}
          setBettingType={setBettingType}
          popularMode={props.popularMode}
          setPopularMode={props.setPopularMode}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />

        <div className="zxsb_content">
          {/* <div className="zxsb_content_header">
            <div className="zxsb_topbar_nav">
              {BETTING_TYPES.filter((item) => item.key !== 'LIVE' || showLiveTab).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`zxsb_topbar_tab ${bettingType === item.key ? 'zxsb_topbar_tab_active' : ''}`}
                  onClick={() => handleModeChange(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="zxsb_topbar_spacer" />
            <div className="zxsb_topbar_search">
              <input
                type="text"
                className="zxsb_search_input"
                placeholder="팀명 검색"
                value={localSearch}
                onChange={(event) => setLocalSearch(event.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <button type="button" className="zxsb_search_btn" onClick={handleSearchSubmit}>검색</button>
              {searchQuery && (
                <button type="button" className="zxsb_search_clear" onClick={() => { setLocalSearch(''); setSearchQuery(''); }}>✕</button>
              )}
            </div>
          </div> */}
          {/* Each Sportsnew page reads real context loading and owns its list
              skeleton. The shell, rail and cart stay mounted during mode
              changes; this content boundary never adds a second loader. */}
          {bettingType === 'INTERNATIONAL' && (
            <InternationalPage
              mobileCartOpen={mobileCartOpen}
              onToggleMobileCart={openMobileCart}
            />
          )}
          {bettingType === 'LIVE' && (
            <InplayPage
              mobileCartOpen={mobileCartOpen}
              onToggleMobileCart={openMobileCart}
            />
          )}
          {bettingType === 'DOMESTIC' && (
            <DomesticPage
              mobileCartOpen={mobileCartOpen}
              onToggleMobileCart={openMobileCart}
            />
          )}
          {bettingType === 'SPECIAL' && (
            <EsportsPage
              mobileCartOpen={mobileCartOpen}
              onToggleMobileCart={openMobileCart}
            />
          )}
        </div>

        <FigmaBetCart
          isMobile={isMobileCart || mobileCartOpen}
          presentationActive={mobileCartOpen || !isMobileCart}
          mobileOpen={mobileCartOpen}
          onMobileClose={closeMobileCart}
          onMobilePresentationBusyChange={setMobileCartBusy}
        />
      </div>
    </div>
  );
};

export default FigmaSportsShell;
